import { EventChannel } from 'redux-saga';
import { delay, fork, put, select, take } from 'typed-redux-saga';

import {
    hideNotification,
    NotificationItem,
    NotificationType,
    notify,
    selectPsNotifications,
} from '../../../shared/features/notifications/ps-notifications';
import { emitterEventChannelFactory } from '../../../shared/helpers/emitterEventChannelFactory';
import { i18n } from '../../../shared/helpers/i18n';
import { NotificationId } from '../types';

const UNMUTE_NOTIFICATION_AUDIO_LEVEL = 0.16;
const UNMUTE_NOTIFICATION_AUDIO_LENGTH = 2;
const UNMUTE_NOTIFICATION_LIFE_TIME = 5000;

/**
 * Показ нотификации
 * Если пользователь находится в другом окне, показываем, дожидаемся пока вернется и скрываем через
 * UNMUTE_NOTIFICATION_LIFE_TIME
 */
function* showUnmuteNotification() {
    yield* put(
        notify({
            id: NotificationId.PLEASE_UNMUTE,
            priority: 1,
            type: NotificationType.INFO,
            text: i18n('statuses', 'please-unmute'),
            iconostasisIcon: {
                name: 'microOff',
                variant: 'solid',
                size: 'md',
            },
            actions: [
                {
                    text: i18n('statuses', 'turn-on'),
                },
            ],
        } as NotificationItem)
    );

    // если пользователь в другой вкладке
    if (document.hidden) {
        const channel = emitterEventChannelFactory(window, 'focus');
        try {
            yield* take(channel);
        } finally {
            channel.close();
        }
    }

    yield* delay(UNMUTE_NOTIFICATION_LIFE_TIME);
    yield* put(hideNotification(NotificationId.PLEASE_UNMUTE));
}

/**
 * Скрытие нотификации
 */
export function* hideUnmuteNotification() {
    yield* put(hideNotification(NotificationId.PLEASE_UNMUTE));
}

/**
 * Непосредственный анализ звука с замьюченным микрофоном + перезапуск в случае обрыва стрима
 */
export function* startAnalyze(audioAnalyzerChannel: EventChannel<number> | undefined): unknown {
    if (!audioAnalyzerChannel) {
        return;
    }

    try {
        let loudAudioCount = 0;

        while (true) {
            const analyzerMessage = yield* take(audioAnalyzerChannel);

            if (analyzerMessage > UNMUTE_NOTIFICATION_AUDIO_LEVEL) {
                loudAudioCount++;
            } else {
                loudAudioCount--;
                if (loudAudioCount < 0) {
                    loudAudioCount = 0;
                }
            }

            const psNotifications = yield* select(selectPsNotifications);

            // Звук превышает пороговое значениие, в конфе более одного человека,
            // отсутствует нотификация о том, что вас замьютил админ
            const shouldShowNotification =
                loudAudioCount > UNMUTE_NOTIFICATION_AUDIO_LENGTH &&
                !psNotifications.items.some((item) => item.id === NotificationId.MODERATION_MUTE_AUDIO);

            if (shouldShowNotification) {
                yield* fork(showUnmuteNotification);
                break;
            }
        }
    } finally {
        audioAnalyzerChannel?.close();
    }
}
